import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';

export interface PalletData {
  sscc: string;
  status: string;
  job_number: string | null;
  customer_code: string | null;
  customer_name: string | null;
  product_code: string | null;
  product_description: string | null;
  forme_number: string | null;
  quantity: number | null;
  pallet_number: number | null;
  total_pallets: number | null;
  machine_code: string | null;
  packed_at: string | null;
  location: string | null;
  is_rescan: boolean;
  has_job_link: boolean;
}

interface PalletDetailCardProps {
  data: PalletData;
}

/**
 * Pallet detail card displayed after an SSCC barcode scan.
 *
 * Shows job number, customer, product, and quantity prominently.
 * SSCC is displayed in small secondary text (operators don't recognise SSCC numbers).
 * Shows "No Job Linked" amber state when packing record has no job match.
 * Shows "Re-scanned" chip when pallet was already registered.
 */
export default function PalletDetailCard({ data }: PalletDetailCardProps) {
  const {
    sscc,
    job_number,
    product_code,
    product_description,
    forme_number,
    quantity,
    pallet_number,
    total_pallets,
    machine_code,
    packed_at,
    location,
    is_rescan,
    has_job_link,
  } = data;

  const formattedPackedAt = packed_at
    ? (() => {
        const parts = new Intl.DateTimeFormat('en-AU', {
          timeZone: 'Australia/Melbourne',
          weekday: 'short',
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).formatToParts(new Date(packed_at));
        const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
        return `${get('weekday').toUpperCase()} ${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`;
      })()
    : null;

  return (
    <Card
      sx={{
        width: '100%',
        border: has_job_link ? '2px solid #2E7D32' : '2px solid #F57C00',
        backgroundColor: '#FFFFFF',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Re-scan indicator */}
        {is_rescan && (
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Chip
              label="Re-scanned"
              size="small"
              color="info"
              variant="outlined"
            />
          </Box>
        )}

        {/* No Job Linked alert */}
        {!has_job_link && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No Job Linked — pallet registered without job data
          </Alert>
        )}

        {/* Job information */}
        {has_job_link && (
          <>
            {/* Job number: full-width hero */}
            <Typography variant="h4" component="div" sx={{ fontWeight: 700, color: '#CB6015', mb: 1.5 }}>
              {job_number}
            </Typography>

            {/* Two-column body: product identity left, pallet-specific right */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
              {/* Left column: product identity */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {product_code && (
                  <Typography variant="h5" component="div" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {product_code}
                  </Typography>
                )}

                {product_description && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                    {product_description}
                  </Typography>
                )}

                {forme_number && (
                  <Typography variant="body1" color="text.secondary">
                    {forme_number}
                  </Typography>
                )}
              </Box>

              {/* Right column: pallet-specific */}
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                {quantity != null && (
                  <Typography variant="h5" component="div" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {quantity.toLocaleString()}
                  </Typography>
                )}

                {pallet_number != null && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                    PALLET NO. {pallet_number}{total_pallets != null ? ` of ${total_pallets}` : ''}
                  </Typography>
                )}

                {(formattedPackedAt || machine_code) && (
                  <Typography variant="body1" color="text.secondary">
                    {[formattedPackedAt, machine_code].filter(Boolean).join(' | ')}
                  </Typography>
                )}

                {location && (
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                    {location}
                  </Typography>
                )}
              </Box>
            </Box>
          </>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* SSCC in small secondary text */}
        <Typography variant="caption" color="text.secondary">
          SSCC: {sscc}
        </Typography>
      </CardContent>
    </Card>
  );
}
